import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCachedResource } from "../hooks/useCachedResource";
import { createProject, listProjects, projectDetail } from "../api/accounting";
import { AcctShell } from "../features/acct/AcctShell";
import { NeuButton, NeuCard, NeuInput } from "../components/neu/Neu";

export default function AcctProjectsPage() {
  const { t } = useTranslation();
  const { data, reload: load } = useCachedResource("acct-projects", () => listProjects().catch(() => []));
  const rows = data || [];
  const [form, setForm] = useState({ name: "", budget: "" });
  const [detail, setDetail] = useState(null);

  const save = async () => {
    await createProject({ name: form.name, budget: Number(form.budget || 0) });
    toast.success(t("acct.projectCreated"));
    setForm({ name: "", budget: "" });
    load();
  };

  return (
    <AcctShell titleKey="projects">
      <NeuCard className="grid gap-3 p-5 sm:grid-cols-3" data-testid="project-form">
        <NeuInput data-testid="project-name" placeholder={t("acct.projectName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <NeuInput data-testid="project-budget" type="number" placeholder={t("acct.budget")} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
        <NeuButton variant="primary" data-testid="project-save" disabled={!form.name} onClick={save}>
          {t("app.save")}
        </NeuButton>
      </NeuCard>

      <div className="grid gap-2" data-testid="project-list">
        {rows.map((row) => (
          <NeuCard key={row.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`project-row-${row.id}`}>
            <span className="min-w-0 flex-1 truncate text-sm font-bold">{row.name}</span>
            <span className="font-mono text-xs" dir="ltr">{row.budget}</span>
            <NeuButton className="px-3 py-2 text-xs" data-testid={`project-open-${row.id}`} onClick={async () => setDetail(await projectDetail(row.id))}>
              {t("acct.details")}
            </NeuButton>
          </NeuCard>
        ))}
      </div>

      {detail ? (
        <NeuCard className="grid gap-2 p-5" data-testid="project-detail">
          <p className="text-sm font-bold">{detail.project.name}</p>
          {[
            ["budget", detail.budget],
            ["actual_spend", detail.actual_spend],
            ["remaining_budget", detail.remaining_budget],
            ["revenue", detail.revenue],
            ["result", detail.result],
            ["completion_percent", detail.completion_percent],
          ].map(([key, value]) => (
            <p key={key} className="flex justify-between gap-3 text-xs" data-testid={`project-${key}`}>
              <span className="text-muted-foreground">{t(`acct.project.${key}`)}</span>
              <span className="font-mono font-bold" dir="ltr">{value}</span>
            </p>
          ))}
        </NeuCard>
      ) : null}
    </AcctShell>
  );
}
